import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { useSelector } from 'react-redux';
import featureEnabled from 'shared/helper/feature';
import { T, useTranslate } from 'shared/helper/translate';
import Link from 'shared/components/Link';
import { getChinaLocalization } from 'shared/account/reducer';
import Popover from 'researcher/components/Popover';
import { getAbstractStatus } from '../reducer';
import { formatForLocale } from '../helpers/date';
import { recordUpgradeNowClick, recordTryForFreeClick } from '../../researcher/helpers/gtm';
export var RubriqAbstractStatusTooltip = function RubriqAbstractStatusTooltip() {
  var _curie$plans;
  var _useSelector = useSelector(function (state) {
      return state;
    }),
    curie = _useSelector.curie;
  var premiumStatus = curie === null || curie === void 0 || (_curie$plans = curie.plans) === null || _curie$plans === void 0 || (_curie$plans = _curie$plans.premium) === null || _curie$plans === void 0 ? void 0 : _curie$plans.status;
  var isCancelledPremium = premiumStatus === 'cancelled';
  var _getAbstractStatus = getAbstractStatus({
      curie: curie
    }),
    hasTrial = _getAbstractStatus.hasTrial,
    hasExpiredTrial = _getAbstractStatus.hasExpiredTrial,
    trialExpirationDate = _getAbstractStatus.trialExpirationDate,
    hasPremium = _getAbstractStatus.hasPremium,
    hasActivePremium = _getAbstractStatus.hasActivePremium,
    hasExpiredPremium = _getAbstractStatus.hasExpiredPremium,
    premiumExpirationDate = _getAbstractStatus.premiumExpirationDate,
    hasPreferredGroup = _getAbstractStatus.hasPreferredGroup,
    hasExpiredPreferredGroup = _getAbstractStatus.hasExpiredPreferredGroup,
    preferredGroupExpirationDate = _getAbstractStatus.preferredGroupExpirationDate;
  var _useTranslate = useTranslate(),
    t = _useTranslate.t;
  var getStatusTooltip = function getStatusTooltip() {
    if (isCancelledPremium) {
      return 'curie.no-access-abstract-tooltip';
    }
    if (hasActivePremium) {
      return 'curie.premium-access-abstract-tooltip';
    }
    if (hasPremium && hasExpiredPremium) {
      return 'curie.premium-expired-abstract-tooltip';
    }
    if (hasPreferredGroup) {
      return hasExpiredPreferredGroup ? 'curie.preferred-group-expired-editing-tooltip' : 'curie.preferred-group-access-abstract-tooltip';
    }
    return 'curie.no-access-abstract-tooltip';
  };
  var getStatusTooltipParams = function getStatusTooltipParams() {
    if (hasTrial) {
      return formatForLocale(trialExpirationDate, t);
    }
    if (hasExpiredPremium) {
      return formatForLocale(premiumExpirationDate, t);
    }
    if (hasExpiredPreferredGroup) {
      return formatForLocale(preferredGroupExpirationDate, t);
    }
    return null;
  };
  return /*#__PURE__*/React.createElement(Popover, {
    content: getStatusTooltip(),
    params: getStatusTooltipParams(),
    "data-bs-toggle": "tooltip",
    "data-bs-placement": "bottom"
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: "fa-light fa-circle-info"
  }));
};
var RubriqAbstractStatus = function RubriqAbstractStatus(_ref) {
  var _curie$plans2;
  var _ref$isNoAbstractMess = _ref.isNoAbstractMessage,
    isNoAbstractMessage = _ref$isNoAbstractMess === void 0 ? false : _ref$isNoAbstractMess;
  var _useSelector2 = useSelector(function (state) {
      return state;
    }),
    curie = _useSelector2.curie;
  var premiumStatus = curie === null || curie === void 0 || (_curie$plans2 = curie.plans) === null || _curie$plans2 === void 0 || (_curie$plans2 = _curie$plans2.premium) === null || _curie$plans2 === void 0 ? void 0 : _curie$plans2.status;
  var isCancelledPremium = premiumStatus === 'cancelled';
  var _getAbstractStatus2 = getAbstractStatus({
      curie: curie
    }),
    noPlan = _getAbstractStatus2.noPlan,
    hasTrial = _getAbstractStatus2.hasTrial,
    hasExpiredTrial = _getAbstractStatus2.hasExpiredTrial,
    trialDaysRemaining = _getAbstractStatus2.trialDaysRemaining,
    hasPremium = _getAbstractStatus2.hasPremium,
    hasActivePremium = _getAbstractStatus2.hasActivePremium,
    hasExpiredPremium = _getAbstractStatus2.hasExpiredPremium,
    hasPreferredGroup = _getAbstractStatus2.hasPreferredGroup,
    hasExpiredPreferredGroup = _getAbstractStatus2.hasExpiredPreferredGroup;
  var hasAbstractAccess = !isCancelledPremium && (hasActivePremium || hasPreferredGroup && !hasExpiredPreferredGroup);
  var hasCheckIcon = hasAbstractAccess;
  var hasLink = !hasAbstractAccess;
  var isChinaUser = useSelector(getChinaLocalization);
  var paddleBillingForChina = featureEnabled('rubriq_china_also_uses_paddle');
  var useCartInsteadOfPaddle = isChinaUser && !paddleBillingForChina;
  var subscriptionRoute = useCartInsteadOfPaddle ? '/rubriq/plan' : '/rubriq/plan';
  var getStatusText = function getStatusText() {
    if (isCancelledPremium) {
      return 'premium required';
    }
    if (hasActivePremium) {
      return 'full access';
    }
    if (hasPremium && hasExpiredPremium) {
      return 'premium plan expired';
    }
    if (hasPreferredGroup) {
      return hasExpiredPreferredGroup ? 'preferred group plan expired' : 'full access';
    }
    return 'premium required';
  };
  var getStatusTextParams = function getStatusTextParams() {
    if (hasTrial && !hasExpiredTrial) {
      return trialDaysRemaining;
    }
    return null;
  };
  var getLinkProps = function getLinkProps() {
    if (useCartInsteadOfPaddle) {
      return {
        to: '/rubriq/plan',
        source: 'try for free'
      };
    }
    return noPlan ? {
      to: '/rubriq/plan',
      source: 'try for free'
    } : {
      to: '/rubriq/plan',
      source: 'upgrade-now'
    };
  };
  var linkProps = getLinkProps();
  var handleUpgradeClick = function handleUpgradeClick() {
    var _document$getElementB;
    var id = linkProps.source === 'upgrade-now' ? 'upgrade_now_abstract_btn' : 'upgrade_try_for_free_abstract_btn';
    var buttonText = (_document$getElementB = document.getElementById(id)) === null || _document$getElementB === void 0 ? void 0 : _document$getElementB.innerText;
    if (linkProps.source === 'upgrade-now') {
      recordUpgradeNowClick({
        buttonText: buttonText
      });
    } else if (linkProps.source === 'try for free') {
      recordTryForFreeClick({
        buttonText: buttonText
      });
    }
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "d-flex flex-column gap-2 flex-md-row gap-md-3 ".concat(isNoAbstractMessage ? 'align-items-center' : 'align-items-start align-items-md-center')
  }, /*#__PURE__*/React.createElement("div", {
    className: "d-flex align-items-center gap-2"
  }, hasCheckIcon && /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: "fa-solid fa-check"
  }), /*#__PURE__*/React.createElement(T, {
    source: getStatusText(),
    params: getStatusTextParams()
  }), /*#__PURE__*/React.createElement(RubriqAbstractStatusTooltip, null)), hasLink && /*#__PURE__*/React.createElement(Link, {
    className: hasCheckIcon && !isNoAbstractMessage ? 'ms-2 ms-md-0 rubriq-status-link' : '',
    to: subscriptionRoute,
    onClick: function onClick() {
      recordUpgradeNowClick({
        buttonText: 'Upgrade Now'
      });
    }
  }, /*#__PURE__*/React.createElement(T, {
    source: "upgrade-now"
  })));
};
export default RubriqAbstractStatus;